import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logout_api } from "../DAL/Login/Login.js";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../hooks/UseAdminContext";

const useInactivityTimeout = (
  totalTimeout = 300 * 1000, // 5 minutes
  alertTimeout = 270 * 1000  // 4.5 minutes (30 second warning)
) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { resetContext } = useAdminContext();
  const totalTimeoutId = useRef(null);
  const alertTimeoutId = useRef(null);
  const countdownTimeoutId = useRef(null);
  const isAlertShown = useRef(false);
  const [showDialog, setShowDialog] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const { isAuthenticated } = useAdminContext();

  const resetTimers = () => {
    if (totalTimeoutId.current) clearTimeout(totalTimeoutId.current);
    if (alertTimeoutId.current) clearTimeout(alertTimeoutId.current);
    if (countdownTimeoutId.current) clearTimeout(countdownTimeoutId.current);
    isAlertShown.current = false;
    setShowDialog(false);
    setCountdown(30);

    alertTimeoutId.current = setTimeout(() => {
      isAlertShown.current = true;
      setShowDialog(true);
      startCountdown();
    }, alertTimeout);

    totalTimeoutId.current = setTimeout(() => {
      handleLogout();
    }, totalTimeout);
  };

  const startCountdown = () => {
    setCountdown(30);
    const startTime = Date.now();
    countdownTimeoutId.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = 30 - elapsed;
      if (remaining <= 0) {
        clearInterval(countdownTimeoutId.current);
        handleLogout();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  };


  const handleLogout = async () => {
    setShowDialog(false);
    clearInterval(countdownTimeoutId.current);
    const result = await logout_api();
    if (result.code === 200) {
      localStorage.clear();
      navigate("/");
      resetContext();
      enqueueSnackbar("Session expired due to inactivity. Logging out...", {
        variant: "info",
      });
    } else {
      enqueueSnackbar(result?.message, { variant: "error" });
    }
  };

  const handleStayLoggedIn = () => {
    setShowDialog(false);
    clearInterval(countdownTimeoutId.current);
    clearTimeout(totalTimeoutId.current);
    clearTimeout(alertTimeoutId.current);
    isAlertShown.current = false;
    setCountdown(30);
    totalTimeoutId.current = setTimeout(() => {
      handleLogout();
    }, 120 * 60 * 1000);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ["mousemove", "click", "keydown", "scroll", "touchstart"];

    const handleActivity = () => {
      if (!isAlertShown.current) {
        resetTimers();
      }
    };

    for (const event of events) {
      window.addEventListener(event, handleActivity);
    }

    resetTimers();

    return () => {
      for (const event of events) {
        window.removeEventListener(event, handleActivity);
      }
      clearTimeout(totalTimeoutId.current);
      clearTimeout(alertTimeoutId.current);
      clearInterval(countdownTimeoutId.current);
    };
  }, [isAuthenticated]);

  return { showDialog, handleStayLoggedIn, handleLogout, countdown };
};

export default useInactivityTimeout;
